"use client";

import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WhatWeDo() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const services = [{
    code: "SVC / 01",
    title: "Influencer Campaigns",
    description: "We help brands reach targeted audiences through creators.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_campaigns.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_campaigns.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/influencer_campaigns.png-wvc-sizes",
    features: ["Targeted reach", "Creator matching", "Platform strategy"]
  }, {
    code: "SVC / 02",
    title: "Campaign Management",
    description: "We handle creator outreach, negotiations, and campaign execution.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/campaign_management.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/campaign_management.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/campaign_management.png-wvc-sizes",
    features: ["End-to-end support", "Contract handling", "Performance tracking"]
  }, {
    code: "SVC / 03",
    title: "Brand Exposure",
    description: "Get your product in front of the right audience at scale.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/brand_exposure.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/brand_exposure.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1704709/img/brand_exposure.webp-wvc-sizes",
    features: ["High-scale reach", "Niche positioning", "Brand awareness"]
  }];
  return <section data-section-id="818" id="what-we-do" className="relative bg-background text-foreground py-20 md:py-32 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div id="what-we-do" className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end mb-16 lg:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/60 mb-6">01 / WHAT WE DO</div>
            <h2 className="font-default font-semibold tracking-tight text-foreground text-[2.5rem] sm:text-5xl lg:text-[4.5rem] leading-[0.95]">
              Strategic influence.
              <br />
              <span className="italic font-serif font-normal">Proven results.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pb-3">
            <p className="font-default text-base lg:text-lg text-foreground/70 leading-relaxed mb-6">
              Pine Playerz Media bridges the gap between corporate scalability and raw digital influence, helping brands reach millions through creator partnerships.
            </p>
            <Link to="#what-we-do" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-foreground border-b border-foreground pb-1 hover:gap-3 transition-all">
              View all services
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((service, i) => <div data-index={i} key={i} className={`group border border-foreground/20 bg-card flex flex-col overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${i === 1 ? "md:translate-y-12" : ""}`} style={{
          aspectRatio: "auto"
        }}>
              {}
              <div className="relative w-full overflow-hidden aspect-[4/3]">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
              </div>

              {}
              <div className="flex-1 flex flex-col p-8">
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-foreground/60">
                    {service.code}
                  </span>
                  <span className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-foreground/20 text-primary transition-all duration-500 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:rotate-45 group-hover:border-primary">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>

                <h3 className="font-default text-2xl md:text-[28px] font-semibold tracking-tight text-foreground leading-tight mb-3">
                  {service.title}
                </h3>
                <p className="font-default text-sm text-foreground/70 leading-relaxed mb-8">
                  {service.description}
                </p>

                <ul className="mt-auto flex flex-col sm:flex-row sm:flex-wrap gap-x-5 gap-y-2 pt-6 border-t border-foreground/10">
                  {service.features.map((feature, fi) => <li data-index={fi} key={fi} className="flex items-center gap-2 font-mono text-[11px] tracking-wide uppercase text-foreground/80">
                      <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-md bg-primary" />
                      {feature}
                    </li>)}
                </ul>
              </div>
            </div>)}
        </div>

        <div className="mt-20 md:mt-28 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-foreground/20">
          <p className="font-default text-sm text-foreground/70 max-w-md">
            Ready to scale your brand? Let's discuss your next influencer campaign.
          </p>
        </div>
      </div>
    </section>;
}